<template>
  <div class="event-block block" :class="[typeCourse, { large }]">
    <div class="online-block" v-if="online"></div>
    <div class="head">
      <TimeDurEvent :event="event" :online="online"/>
      <div
          class="badge"
          :style="`background-color: ${colorBadgeTypeEvent.bg}; color: ${colorBadgeTypeEvent.text}`"
          v-if="typeCourseName"
          v-text="typeCourseName"
      />
    </div>

    <div class="title" v-text="$localized(event.localized_name, event.name)"/>
    <div class="hosts">
      <div class="user" v-for="user in users" :key="user.id">
        <Avatar size="sm" :src="user.avatar || user.file" :online="user.online"/>
        <div class="name">{{$localized(user.localized_name, user.first_name)}} {{$localized(user.localized_surname, user.last_name)}}</div>
      </div>
      <a
          v-if="needLink && isActualEvent"
          :href="event.zoom_link"
          target="_blank"
          class="user add"
      >
        <Avatar size="sm" :src="require('@/assets/icons/arrow-r-dark.svg')" class="size-auto"/>
        <div class="name">{{ $t('events.event.join-guests') }}</div>
      </a>
      <a
          v-if="needLink && event.internal_link && isActualEvent"
          :href="event.internal_link"
          target="_blank"
          class="user add"
      >
        <Avatar size="sm" :src="require('@/assets/icons/arrow-r-dark.svg')" class="size-auto"/>
        <div class="name">{{ $t('events.event.join-users') }}</div>
      </a>
    </div>
    <div class="footer">
      <div v-if="event.video_link" class="event-link" :class="{disabled: !event.video_link}" @click="watchVideo">
        <button class="no-img">
          <Play/>
        </button>
        <span>{{ $t('events.event.watch-video') }}</span>
      </div>
      <div v-else-if="event.zoom_meeting_number" class="event-link" :class="{disabled: !online}" @click="watchZoom">
        <button class="primary" :class="{disabled: !online}">
          <ArrowRight class="event-block__join-icon"/>
        </button>
        <span>{{ startButtonText }}</span>
      </div>
      <a v-else-if="event.zoom_link" :href="event.zoom_link" target="_blank" class="event-link" :class="{disabled: !online}">
        <button class="primary" :class="{disabled: !online}">
          <ArrowRight class="event-block__join-icon"/>
        </button>
        <span>{{ startButtonText }}</span>
      </a>
      <div v-else class="event-link" :class="{disabled: true}">
        <button class="primary" :class="{disabled: true}">
          <ArrowRight class="event-block__join-icon"/>
        </button>
        <span>{{ startButtonText }}</span>
      </div>
      <div class="footer-info">
        <AttachmentCount :library="event.library"/>

        <Rating :id="event.id" :rating="event.rating" :extreme="extreme"/>
      </div>

    </div>
  </div>
</template>

<script>
import Avatar from '@/components/user/Avatar'
import TimeDurEvent from '@/components/events/TimeDurEvent'
import Rating from '@/components/events/Rating'
import {mapState} from 'vuex'
import _inRange from 'lodash/_baseInRange'
import isActualEvent from '../../mixins/isActualEvent'
import AttachmentCount from '@/components/events/AttachmentCount'
import ArrowRight from "@/components/icons/ArrowRight";
import Play from "@/components/icons/Play";
import eventUtils from '@/mixins/eventUtils'

export default {
  name: 'Event',
  components: {Play, ArrowRight, AttachmentCount, Rating, TimeDurEvent, Avatar},
  mixins: [isActualEvent, eventUtils],
  props: {
    event: Object,
    extreme: Boolean,
    large: Boolean,
  },
  computed: {
    online() {
      const {time_start, time_finish} = this.event
      return _inRange(this.currentTime, time_start, time_finish)
    },
    special() {
      return this.event.course?.id === 2
    },
    needLink() {
      return this.event.type?.id !== 1
    },
    typeCourseName() {
      return this.event.type?.title
    },
    typeCourse() {
      return this.special ? 'special' : 'general'
    },
    users() {
      const event = this.event
      if (this.event.user) {
        return [event.user, ...event.experts]
      } else {
        return event.experts || []
      }
    },
    isAuthor() {
      return this.event.user?.id === this.$auth?.user()?.id;
    },
    startButtonText() {
      return this.isAuthor ? this.$t('startEvent') : this.$t('joinEvent');
    },
    ...mapState(['currentTime']),
  },
  methods: {
    watchZoom() {
      this.$emit('watch-zoom', this.event)
    },
    watchVideo() {
      this.$emit('watch-video', this.event)
    },
  },
}
</script>

<style lang="scss">
.event-block {
  position: relative;
  z-index: 2;
  padding: 23px 20px 30px 21px;
  flex-shrink: 0;
  margin: 20px 0;

  @include mobile {
    padding-bottom: 25px;
  }

  &:first-child {
    margin-top: 0 !important;
  }

  &:hover {
    z-index: 3;
  }

  &.large {
    padding: 32px 43px 32px 32px;
    margin: 18px 0;

    .head {
      margin-bottom: 14px;
    }

    .hosts {
      margin-bottom: 29px;
    }

    .footer {
      align-items: flex-end;

      .footer-info {
        margin-bottom: 2px;
      }
    }
  }

  &__join-icon {
    margin-right: 0;
  }

  .primary {
    padding: 10px;
  }

  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 23px;

    & > *:first-child:last-child {
      justify-self: flex-end;
      margin-left: auto;
    }
  }

  .title {
    font-family: 'Source Serif Pro', serif;
    font-weight: bold;
    font-size: 16px;
    line-height: 19px;
    margin-bottom: 15px;
  }

  .hosts {
    margin-bottom: 25px;

    @include mobile {
      margin-bottom: 33px;
    }

    .user {
      display: flex;
      align-items: center;
      margin-bottom: 5px;
      font-weight: 400;
      font-size: 15px;
      line-height: 125%;
      color: $text;

      .avatar {
        margin-right: 15px;
      }

      &.add {
        margin-top: 15px;

        .name {
          font-weight: normal;
          color: $text;
          line-height: 1;
          text-decoration: underline;
          text-decoration-style: dashed;
          text-decoration-color: $dark;
        }
      }
    }
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include mobile {
      display: block;
    }

    .event-link {
      display: flex;
      align-items: center;
      cursor: pointer;
      appearance: none;
      text-decoration: none !important;

      @include mobile {
        margin-bottom: 20px;
      }
      svg {
        width: 8px;
        height: 8px;
        margin: 0 !important;
      }

      button {
        width: 30px;
        height: 30px;
        padding: 0 !important;
        flex: none;
      }

      span {
        margin-left: 10px;
        font-weight: 600;
        font-size: 12px;
        color: $primary;
      }
      &.disabled {
        pointer-events: none;
        button {
          background: #CCD7E64D;
          svg {
            color: $dark;
          }
        }
        span {
          color: $dark;
        }
      }
    }

    .footer-info {
      display: flex;
      align-items: center;
    }
  }

  .badge {
    text-transform: capitalize;
  }

  .online-block {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    overflow: hidden;
    border-radius: 20px;
    height: 40px;
    pointer-events: none;

    &::before {
      content: '';
      display: block;
      width: 50%;
      height: 2px;
      background: $primary;
    }
  }

  &.general {
    .online-status-dote.online {
      background: $primary;
    }
  }

  &.special {
    .online-status-dote.online {
      background: $primary;
    }
  }

  &.vertical {
    margin: 0 4px;
  }

  @include PC {
    &.vertical {
      display: flex;
      flex-direction: column;
      width: 212px;
      height: 482px;
      padding: 16px 20px;
      padding-top: 100px;

      .title {
        min-height: 57px;
        margin-bottom: 42px;
      }

      .head {
        position: absolute;
        top: 16px;
        right: 20px;
        left: 20px;
        flex-direction: column;

        .time-dur-event {
          align-self: flex-end;
          margin-bottom: 10px;
        }

        .badge {
          order: 2;
        }
      }

      .hosts {
        height: 100%;
        flex-shrink: 2;
      }
    }
  }
}
</style>
